import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/styles/webadminmanageuser.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";

// Your shell components (remove or replace if not present)
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import userService from "../../services/userService";
import adminStaffService from "../../services/adminStaffService";

// Assets (replace filenames with the real ones in your project)
import webadminfilter from "../assets/img/webadminfilter.svg";
// import AddIcon from "../assets/img/add-user.svg"; // optional small icon for add button
import webadduser from "../assets/img/webadduser.svg"
import arrowleftt from "../assets/img/arrowleftt.svg"
import close from "../assets/img/close-square.svg";

export default function ManageUsers() {
  const navigate = useNavigate();
  // modal state
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // NEW: confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Backend integration (like mobile app)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);

  // Search and filtering (like mobile app)
  const [searchQuery, setSearchQuery] = useState("");

  // editing / add form state
  const [editingUser, setEditingUser] = useState(null); // user object when editing
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    userType: "", // admin, manager, support, marketing, operator - like mobile app
  });


  // filter form state
  const [filterType, setFilterType] = useState("");
  const [deletingUser, setDeletingUser] = useState(null);
  const [staffStatusMap, setStaffStatusMap] = useState({});


  // Load users from API (matching React Native exactly)
  useEffect(() => {
    fetchUsers();
    fetchStaffStatus();
  }, [searchQuery, filterType]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllAdminUsers({
        search: searchQuery,
        role: filterType || undefined
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load users');
      }
      const fetchedUsers = response?.data?.users || response?.users || [];
      setUsers(Array.isArray(fetchedUsers) ? fetchedUsers : []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffStatus = async () => {
    try {
      const response = await adminStaffService.getAllStaffStatus();
      const data = response?.data?.data || response?.data || [];
      const map = {};
      (Array.isArray(data) ? data : []).forEach(s => {
        map[s.id] = s.availability || {};
      });
      setStaffStatusMap(map);
    } catch (e) {
      console.error('Error fetching staff status:', e);
    }
  };

  const handleToggleOnline = async (user, isCurrentlyOnline) => {
    try {
      if (isCurrentlyOnline) {
        await adminStaffService.goOffline(user.id);
      } else {
        await adminStaffService.goOnline(user.id);
      }
      fetchStaffStatus();
    } catch (e) {
      console.error('Error toggling staff status:', e);
      toast.error('Failed to update staff status');
    }
  };

  // Generate password function matching React Native
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const length = Math.floor(Math.random() * (12 - 8 + 1)) + 8; // length between 8 and 12
    let pass = '';
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm(prev => ({ ...prev, password: pass }));
  };

  // Copy to clipboard function
  const copyToClipboard = async () => {
    if (form.password) {
      try {
        await navigator.clipboard.writeText(form.password);
        toast.success('Password copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  // Get role display matching React Native
  const getRoleDisplay = (role) => {
    switch (role) {
      case 'customer_support': return { label: 'Customer Support', color: '#4995FF', bg: '#4995FF1A' };
      case 'marketing': return { label: 'Marketing', color: '#FACC15', bg: '#FACC151A' };
      case 'dashboard_view': return { label: 'Dashboard View', color: '#6C5DD3', bg: '#6C5DD31A' };
      case 'accounts': return { label: 'Accounts', color: '#FF754C', bg: '#FF754C1A' };
      default: return { label: role || 'User', color: '#666', bg: '#eee' };
    }
  };

  // open Add modal and reset form - matching React Native
  function openAddModal() {
    setEditingUser(null);
    resetForm();
    generatePassword();
    setAddOpen(true);
  }

  // open Edit modal for given user - matching React Native
  function openEditModal(user) {
    setEditingUser(user);
    setForm({
      username: user.full_name || user.name || '',
      email: user.email,
      password: user.raw_password || "",
      userType: user.role
    });
    setEditOpen(true);
  }


  // Handle add user - matching React Native exactly
  const handleAddUser = async () => {
    if (!form.username || !form.email || !form.password || !form.userType) {
      toast.warning("Please fill all fields and select access type");
      return;
    }
    try {
      const payload = {
        name: form.username,
        email: form.email,
        password: form.password,
        adminUserType: form.userType
      };
      const response = await userService.createAdminUser(payload);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to add user');
      }
      toast.success("User added successfully");
      setAddOpen(false);
      resetForm();
      fetchUsers();
    } catch (e) {
      toast.error("Error adding user");
      console.error(e);
    }
  };

  // Handle update user - matching React Native exactly
  const handleUpdateUser = async () => {
    if (!form.username || !form.email || !form.userType) {
      toast.warning("Please fill all fields and select access type");
      return;
    }
    try {
      const payload = {
        name: form.username,
        email: form.email,
        adminUserType: form.userType,
      };
      if (form.password) payload.password = form.password;
      const response = await userService.updateAdminUser(editingUser.id, payload);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to update user');
      }
      toast.success("User updated successfully");
      setEditOpen(false);
      resetForm();
      fetchUsers();
    } catch (e) {
      toast.error("Error updating user");
      console.error(e);
    }
  };

  // Delete user - matching React Native exactly
  const handleDeleteUser = async () => {
    if (!deletingUser?.id) {
      toast.error("Unable to delete user");
      return;
    }
    try {
      const response = await userService.deleteAdminUser(deletingUser.id);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete user');
      }
      toast.success("User deleted successfully");
      setConfirmOpen(false);
      setDeletingUser(null);
      setEditOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error("Error deleting user");
      console.error(e);
    }
  };

  // Reset form function
  const resetForm = () => {
    setForm({
      username: "",
      email: "",
      password: "",
      userType: "",
    });
  };

  // Filter handlers
  function handleResetFilter() {
    setFilterType("");
  }
  function handleApplyFilter() {
    // apply filter logic here — for demo we just close
    setFilterOpen(false);
  }


  return (
    <div className="mu-page d-flex">
      <LeftNav />

      <div className="mu-content-wrap flex-grow-1">
        <TopNav />

        <div className="p-4">
          <div className="card mu-container-card ">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mu-header-row">
                <div className="d-flex align-items-center gap-2">
                  <img src={arrowleftt} onClick={() => navigate("/adminprofile")} style={{ cursor: "pointer" }}></img>
                  <h2 className="mu-title mb-0">Manage Users</h2>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="mu-total">Total Users : <span className="mu-total-num">{loading ? '...' : (users?.length || 0)}</span></div>

                  <button
                    className="btn btn-outline-secondary mu-filters-btn"
                    onClick={() => setFilterOpen(true)}
                    title="Filters"
                  >
                    {webadminfilter && <img src={webadminfilter} alt="filters" className="mu-icon-sm" />}
                    <span className="mu-filter-text">Filters</span>
                  </button>
                </div>
              </div>

              {/* Search Bar like mobile app */}
              <div className="mt-3 mb-3">
                <div className="d-flex gap-2">
                  <div className="flex-grow-1 position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search users by name, email or role..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        paddingLeft: '40px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        height: '44px'
                      }}
                    />
                    <span
                      className="position-absolute"
                      style={{
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF'
                      }}
                    >
                      🔍
                    </span>
                  </div>
                </div>
              </div>

              {/* Total users count - matching React Native */}
              <div className="mb-3" style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#000' }}>
                  Total users : {loading ? '...' : (users?.length || 0)}
                </span>
              </div>

              {/* User list - matching React Native style */}
              <div className="mu-list mt-3" style={{ marginBottom: "10px" }}>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <div className="text-danger mb-3">❌ {error}</div>
                    <button className="btn btn-primary" onClick={fetchUsers}>
                      Retry
                    </button>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-5">
                    <h5 className="text-muted">No users found</h5>
                    <p className="text-muted">No users available.</p>
                  </div>
                ) : (
                  users.map((user, index) => {
                    const roleData = getRoleDisplay(user.role);
                    return (
                      <div
                        key={user.id || index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: '#fff',
                          padding: '16px',
                          margin: '10px 0',
                          borderRadius: '10px',
                          borderColor: '#E2E4E9',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          marginBottom: '1px'
                        }}
                      >
                        <div>
                          <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#000' }}>
                            {user.full_name || user.name || 'Unknown'}
                          </div>
                          <div style={{ marginBottom: '5px', fontWeight: '500', color: '#8C8C8C', fontSize: '12px' }}>
                            {user.email}
                          </div>
                          <div
                            style={{
                              backgroundColor: roleData.bg,
                              paddingTop: '3px',
                              paddingBottom: '3px',
                              paddingLeft: '10px',
                              paddingRight: '10px',
                              borderRadius: '13px',
                              display: 'inline-block'
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                color: roleData.color,
                                fontSize: '12px',
                                textAlign: 'center'
                              }}
                            >
                              {roleData.label}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <button
                            style={{
                              backgroundColor: '#fff',
                              paddingTop: '8px',
                              paddingBottom: '8px',
                              paddingLeft: '16px',
                              paddingRight: '16px',
                              borderRadius: '8px',
                              borderColor: '#E2E4E9',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              marginRight: '8px'
                            }}
                            onClick={() => openEditModal(user)}
                          >
                            <span style={{ fontWeight: 'bold', color: '#414141' }}>Edit</span>
                          </button>
                          <button
                            style={{
                              backgroundColor: '#fff',
                              paddingTop: '8px',
                              paddingBottom: '8px',
                              paddingLeft: '12px',
                              paddingRight: '12px',
                              borderRadius: '8px',
                              borderColor: '#E2E4E9',
                              borderWidth: '1px',
                              borderStyle: 'solid'
                            }}
                            onClick={() => {
                              setDeletingUser(user);
                              setConfirmOpen(true);
                            }}
                          >
                            <IoTrashOutline size={18} color="#DC2626" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Floating Add User button positioned fixed at bottom-right (page end) */}
      <div className="mu-floating-add">
        <button className="btn btn-dark mu-main-add-btn" onClick={openAddModal}>
          {webadduser && <img src={webadduser} alt="add" className="mu-icon-sm" />}
          <span className="ms-2">+ Add User</span>
        </button>
      </div>

      {/* FILTER MODAL */}
      {filterOpen && (
        <div className="mu-modal-overlay" onClick={() => setFilterOpen(false)}>
          <div className="mu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mu-modal-header">
              <h5>Filters</h5>
              <button className="mu-modal-close" onClick={() => setFilterOpen(false)}><img src={close} alt="Close" /></button>
            </div>

            <div className="mu-modal-body">
              <label className="mu-muted-label">User Type</label>
              <select className="form-select mu-modal-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="">All</option>
                <option value="customer_support">Customer Support</option>
                <option value="marketing">Marketing</option>
                <option value="dashboard_view">Dashboard View</option>
                <option value="accounts">Accounts</option>
              </select>
            </div>

            <div className="mu-modal-actions">
              <button className="btn btn-outline-primary mu-btn-reset" onClick={handleResetFilter}>Reset</button>
              <button className="btn btn-primary mu-btn-apply" onClick={handleApplyFilter}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD MODAL - Updated to match mobile app */}
      {addOpen && (
        <div className="mu-modal-overlay" onClick={() => setAddOpen(false)}>
          <div className="mu-modal wide" onClick={(e) => e.stopPropagation()}>
            <div className="mu-modal-header">
              <h5>Add User</h5>
              <button style={{ border: "none", background: "#fff" }} onClick={() => setAddOpen(false)}> <img src={close} alt="Close" /></button>
            </div>

            <div className="mu-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Username - matching React Native */}
              <label className="mu-label">Username</label>
              <input
                className="form-control mu-input"
                placeholder="Enter Username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />

              {/* Email - matching React Native */}
              <label className="mu-label mt-3">Email</label>
              <input
                className="form-control mu-input"
                placeholder="Enter Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              {/* Password with Auto Generate - matching React Native */}
              <label className="mu-label mt-3">Password</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '6px', padding: '0' }}>
                <input
                  className="form-control"
                  style={{ border: 'none', flex: 1, padding: '10px 15px' }}
                  placeholder="Enter or Generate Password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{ padding: '10px', border: 'none', background: 'transparent' }}
                  title="Copy password"
                >
                  📋
                </button>
                <button
                  type="button"
                  onClick={generatePassword}
                  style={{ padding: '10px', border: 'none', background: 'transparent' }}
                  title="Generate password"
                >
                  🔄
                </button>
              </div>

              {/* User Access Type - matching React Native exactly */}
              <label className="mu-label mt-4" style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>
                User Access Type
              </label>
              <div className="mu-usertype-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '10px' }}>
                <div
                  onClick={() => setForm({ ...form, userType: 'customer_support' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'customer_support' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'customer_support' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Customer Support</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'marketing' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'marketing' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'marketing' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Marketing</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'dashboard_view' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'dashboard_view' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'dashboard_view' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Dashboard View</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'accounts' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'accounts' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'accounts' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Accounts</span>
                </div>
              </div>

              {/* Permission descriptions matching React Native */}
              {form.userType === 'customer_support' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Customer Support Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Task Manager & CRM
                  </div>
                </div>
              )}
              {form.userType === 'marketing' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Marketing Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Push Notifications
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Push Notification Reports
                  </div>
                </div>
              )}
              {form.userType === 'dashboard_view' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Dashboard Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Only Dashboard view
                  </div>
                </div>
              )}
              {form.userType === 'accounts' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Accounts Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Invoices and Billings
                  </div>
                </div>
              )}

            </div>

            <div className="mu-modal-actions" style={{ justifyContent: 'space-between', padding: '16px' }}>
              <button
                className="btn"
                onClick={() => setAddOpen(false)}
                style={{
                  padding: '12px 20px',
                  borderColor: '#6F70FA',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderRadius: '8px',
                  color: '#6F70FA',
                  backgroundColor: 'white',
                  fontWeight: '600',
                  width: '40%'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddUser}
                style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #9292FF, #6F70FA)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  width: '50%'
                }}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editOpen && (
        <div className="mu-modal-overlay" onClick={() => { setEditOpen(false); setEditingUser(null); }}>
          <div className="mu-modal wide" onClick={(e) => e.stopPropagation()}>
            <div className="mu-modal-header">
              <h5>Edit user</h5>
              <button className="mu-modal-close" onClick={() => { setEditOpen(false); setEditingUser(null); }}> <img src={close} alt="Close" /></button>
            </div>

            <div className="mu-modal-body">
              <label className="mu-label">Username</label>
              <input className="form-control mu-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />

              <label className="mu-label mt-3">Email ID</label>
              <input className="form-control mu-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

              {/* Password (Optional update) - matching React Native */}
              <label className="mu-label mt-3">Password (Leave blank to keep current)</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '6px', padding: '0' }}>
                <input
                  className="form-control"
                  style={{ border: 'none', flex: 1, padding: '10px 15px' }}
                  placeholder="Enter or Generate Password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{ padding: '10px', border: 'none', background: 'transparent' }}
                  title="Copy password"
                >
                  📋
                </button>
                <button
                  type="button"
                  onClick={generatePassword}
                  style={{ padding: '10px', border: 'none', background: 'transparent' }}
                  title="Generate password"
                >
                  🔄
                </button>
              </div>


              {/* User Access Type - matching React Native exactly */}
              <label className="mu-label mt-4" style={{ fontWeight: '600', marginBottom: '10px', fontSize: '16px' }}>
                User Access Type
              </label>
              <div className="mu-usertype-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '10px' }}>
                <div
                  onClick={() => setForm({ ...form, userType: 'customer_support' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'customer_support' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'customer_support' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Customer Support</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'marketing' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'marketing' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'marketing' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Marketing</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'dashboard_view' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'dashboard_view' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'dashboard_view' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Dashboard View</span>
                </div>

                <div
                  onClick={() => setForm({ ...form, userType: 'accounts' })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    border: '1px solid #EBEBEB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: form.userType === 'accounts' ? '#f0f0f0' : 'white'
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '10px',
                    border: '2px solid #007AFF',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {form.userType === 'accounts' && (
                      <div style={{ width: '10px', height: '10px', borderRadius: '5px', backgroundColor: '#007AFF' }} />
                    )}
                  </div>
                  <span style={{ fontSize: '15px', fontWeight: '500', color: '#242424' }}>Accounts</span>
                </div>
              </div>

              {/* Permission descriptions matching React Native */}
              {form.userType === 'customer_support' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Customer Support Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Task Manager & CRM
                  </div>
                </div>
              )}
              {form.userType === 'marketing' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Marketing Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Push Notifications
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Push Notification Reports
                  </div>
                </div>
              )}
              {form.userType === 'dashboard_view' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Dashboard Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Only Dashboard view
                  </div>
                </div>
              )}
              {form.userType === 'accounts' && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '16px', color: '#323232', fontWeight: '400', marginBottom: '4px' }}>
                    Accounts Permission
                  </div>
                  <div style={{ fontSize: '14px', color: '#5455FF', fontWeight: '400', marginBottom: '4px' }}>
                    • Access to Invoices and Billings
                  </div>
                </div>
              )}
            </div>

            {/* Save & Cancel Row - matching React Native */}
            <div className="mu-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
              <button
                className="btn btn-outline-danger"
                onClick={() => {
                  setDeletingUser(editingUser);
                  setConfirmOpen(true);
                }}
                style={{
                  padding: '12px 20px',
                  borderColor: '#FF3223',
                  color: '#FF3223',
                  fontWeight: '600',
                  width: '40%'
                }}
              >
                Delete
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setEditOpen(false)}
                  style={{ padding: '12px 20px', fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateUser}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #9292FF, #6F70FA)',
                    border: 'none',
                    color: 'white',
                    fontWeight: '600'
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal - matching React Native exactly */}
      {confirmOpen && (
        <div className="mu-modal-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="mu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mu-modal-header" style={{ justifyContent: 'center' }}>
              <h5 className="mb-0" style={{ color: '#000000', fontSize: '18px', fontWeight: 'bold' }}>Delete User</h5>
            </div>

            <div className="mu-modal-body">
              <p style={{ fontSize: '14px', color: '#333', marginBottom: '20px' }}>
                Are you sure you want to delete this user?
              </p>
            </div>

            <div className="mu-modal-actions" style={{ justifyContent: 'space-between', padding: '16px' }}>
              <button
                className="btn"
                onClick={() => setConfirmOpen(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#EBEBEB',
                  padding: '12px',
                  borderRadius: '8px',
                  marginRight: '10px',
                  color: '#000',
                  fontWeight: '600',
                  border: 'none'
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleDeleteUser}
                style={{
                  flex: 1,
                  backgroundColor: '#FF6D62',
                  padding: '12px',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  border: 'none'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
